import { useCallback, useEffect, useRef, useState } from "react"

import { gameService } from "services/game/game.service"
import { GameInterface } from "services/game/type"

interface UseGameSearchOptions {
  pageSize?: number
  includeDisabled?: boolean
  minChars?: number
  excludeIds?: string[]
  debounceMs?: number
}

interface UseGameSearchReturn {
  query: string
  setQuery: (q: string) => void
  results: GameInterface[]
  loading: boolean
  clear: () => void
}

export function useGameSearch({
  pageSize = 20,
  includeDisabled = false,
  minChars = 2,
  excludeIds,
  debounceMs = 300
}: UseGameSearchOptions = {}): UseGameSearchReturn {
  const [query, setQueryState] = useState("")
  const [results, setResults] = useState<GameInterface[]>([])
  const [loading, setLoading] = useState(false)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const genRef = useRef(0)
  // Keep options in a ref so setQuery stays stable without listing them as deps.
  const optsRef = useRef({ pageSize, includeDisabled, minChars, excludeIds, debounceMs })
  optsRef.current = { pageSize, includeDisabled, minChars, excludeIds, debounceMs }

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    }
  }, [])

  const setQuery = useCallback((q: string) => {
    const { pageSize, includeDisabled, minChars, excludeIds, debounceMs } = optsRef.current
    setQueryState(q)
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    if (q.trim().length < minChars) {
      setResults([])
      return
    }
    timerRef.current = setTimeout(async () => {
      const gen = ++genRef.current
      setLoading(true)
      const data = await gameService.getAllGames({ page: 1, pageSize, search: q.trim(), includeDisabled })
      if (gen !== genRef.current) return
      setLoading(false)
      if (!data) return
      const excluded = excludeIds ? new Set(excludeIds) : null
      const games = (data.games ?? []) as GameInterface[]
      setResults(excluded ? games.filter((g) => !excluded.has(g.identifier)) : games)
    }, debounceMs)
  }, [])

  const clear = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    ++genRef.current
    setQueryState("")
    setResults([])
    setLoading(false)
  }, [])

  return { query, setQuery, results, loading, clear }
}
